
import { useState, useEffect } from 'react';
import { handlePhoneClick } from '../../../utils/trackPhoneCall';
import { handleQuoteClick } from '../../../utils/quoteCta';
import { siteConfig } from '../../../config/site';
import type { Zona } from '../../../data/zonas';

const HeroSection = ({ zona }: { zona?: Zona } = {}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [textVisible, setTextVisible] = useState(true);

  const isDesentupimentosSite = siteConfig.domain === 'alphadesentupimentos.pt';
  const isEletricaSite = siteConfig.domain === 'alphaeletrica.pt' || siteConfig.domain === 'alphaeletric.pt';
  const isEletrodomesticosSite = siteConfig.domain === 'alphaservices.pt';
  const isClimatizacaoSite = siteConfig.domain === 'alphaclimatizacao.pt';
  const isCleanSite = siteConfig.domain === 'alphaclean.pt';
  // Nas páginas de zona a localidade substitui a cidade em todos os textos
  const cityName = zona ? zona.nome : siteConfig.address.city;
  const prep = zona ? zona.preposicao : 'em';

  // Headline + background image - diferentes para cada site
  const slides = isCleanSite ? [
    {
      text: `Limpeza Profissional em ${cityName}`,
      subtext: "Condomínios, escritórios, casas, pós-obra e comércio com equipas fixas",
      image: "/images/limpeza-hero.webp",
    },
    {
      text: "Condomínios e Pós-Obra",
      subtext: "Equipas identificadas, produtos incluídos e valor fechado",
      image: "/images/limpeza-condominios.webp",
    },
    {
      text: "Alojamento Local e Restaurantes",
      subtext: "Higienização rigorosa e cumprimento de prazos exigentes",
      image: "/images/limpeza-alojamento-local.webp",
    },
  ] : isClimatizacaoSite ? [
    {
      text: `Climatização ${prep} ${cityName}`,
      subtext: `Instalação de Ar Condicionado e Manutenção ${prep} ${cityName}`,
      image: "/images/climatizacao-hero.webp",
    },
  ] : isDesentupimentosSite ? [
    {
      text: `Desentupimentos Urgentes 24h em ${cityName}`,
      subtext: "Equipamentos profissionais e técnicos experientes",
      image: "/images/vans-alpha-desentupimentos.webp",
    },
    {
      text: "Equipamentos de Última Geração",
      subtext: "Soluções eficazes para todos os tipos de entupimentos",
      image: "/images/desentupimento-hidrojato-real.webp",
    },
    {
      text: "Vídeo Inspeção de Tubulações",
      subtext: "Diagnóstico preciso sem necessidade de obras",
      image: "/images/caminhao-alpha-hero.webp",
    },
  ] : isEletricaSite ? [
    {
      text: "Instalações e Reparações Elétricas em Lisboa",
      subtext: "Serviço profissional com certificação e garantia",
      image: "/images/instalacao-eletrica.webp",
    },
    {
      text: "Urgências Elétricas 24H",
      subtext: "Eletricistas certificados prontos a ajudar",
      image: "/images/reparacao-eletrica.webp",
    },
    {
      text: "Iluminação LED e Quadros Elétricos",
      subtext: "Modernize a sua casa com segurança e eficiência",
      image: "/images/quadro-eletrico.webp",
    },
  ] : isEletrodomesticosSite ? [
    {
      text: "Instalação Profissional de Eletrodomésticos",
      subtext: "Placas, fornos, máquinas e muito mais com garantia",
      image: "/images/alpha services/fogao eletrico.webp",
    },
    {
      text: "Agendamento Rápido e Preços Fixos",
      subtext: "Saiba quanto vai pagar antes mesmo de começar",
      image: "/images/alpha services/Máquina de Lavar Roupa.webp",
    }
  ] : [
    {
      text: "Detecção e Reparação de Fugas de Água",
      subtext: "Peça já o seu orçamento — resposta rápida e sem compromisso",
      image: "/images/fuga-detecao.webp",
    },
  ];

  // Ajuste fino de enquadramento por slide - foco nos rostos das pessoas
  const bgPositions = isCleanSite ? [
    'center 30%', // hero - foco na profissional
    'center 40%',
    'center 30%', // alojamento local - mesma foto do hero
  ] : isClimatizacaoSite ? [
    'center 40%',
    'center 30%',
    'center 40%',
  ] : isDesentupimentosSite ? [
    'center 30%', // desentupimentos - foco no técnico
    'center 30%', // hidrojato - foco no técnico
    'center 30%', // vídeo inspeção
  ] : isEletricaSite ? [
    'center 30%',
    'center 30%',
    'center 30%',
  ] : isEletrodomesticosSite ? [
    'center 30%',
    'center 30%',
  ] : [
    'center 30%', // detecção de fugas
    'center 30%',
    'center 30%',
    'center 30%',
    'center 30%',
    'center 30%',
    'center 30%',
  ];

  // Em alguns slides movemos o texto para a direita em telas grandes
  const textOnRight = currentSlide === 1;

  useEffect(() => {
    // Capa estática quando só existe uma imagem
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      // animação de saída
      setTextVisible(false);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        // animação de entrada
        setTextVisible(true);
      }, 150);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="relative min-h-screen flex items-end lg:items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-no-repeat transition-[background-image] duration-500"
        style={{
          backgroundImage: `url('${slides[currentSlide].image}')`,
          backgroundPosition: bgPositions[currentSlide],
          imageRendering: '-webkit-optimize-contrast',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
        }}
      />
      {/* Filtro escuro para destacar o texto */}
      <div className="absolute inset-0 bg-black/45" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pb-0">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`text-white ${textOnRight ? 'lg:col-start-2' : ''}`}>
            <h1 className={`text-2xl sm:text-3xl lg:text-6xl font-bold leading-tight mb-4 transition-all duration-500 ${textVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}>
              <span
                className="block"
                style={{
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.8), 0 0 20px rgba(255, 255, 255, 0.6), 0 0 30px rgba(255, 255, 255, 0.4), 0 2px 8px rgba(0, 0, 0, 0.6)'
                }}
              >
                {slides[currentSlide].text}
              </span>
            </h1>
            <p
              className={`text-base sm:text-lg lg:text-2xl text-white transition-all duration-500 ${textVisible ? 'opacity-90 translate-x-0' : 'opacity-0 translate-x-6'}`}
              style={{
                textShadow: '0 0 8px rgba(255, 255, 255, 0.7), 0 0 15px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(0, 0, 0, 0.5)'
              }}
            >
              {slides[currentSlide].subtext || 'Peça já o seu orçamento — resposta rápida e sem compromisso'}
            </p>

            <div className={`mt-6 flex flex-wrap gap-4 transition-all duration-500 ${textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
              <a
                href={`tel:${siteConfig.phone}`}
                onClick={handlePhoneClick}
                className="bg-white text-blue-700 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                {isEletricaSite ? 'Emergência 24/7:' : 'Ligar agora:'} {siteConfig.phoneDisplay}
              </a>
              <a
                href={isClimatizacaoSite ? '#orcamento' : `tel:${siteConfig.phone}`}
                onClick={isClimatizacaoSite ? handleQuoteClick : handlePhoneClick}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <i className={isClimatizacaoSite ? 'ri-mail-send-line' : 'ri-phone-line'}></i>
                Pedir um orçamento
              </a>
            </div>



            {/* Slide Indicators */}
            {slides.length > 1 && (
              <div className="flex gap-2 mb-8 mt-6">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-white' : 'bg-white/30'
                      }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column empty to keep layout or can be removed on small screens */}
          <div className="hidden lg:block"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
